/**
 * Money, quantity and rate handling.
 *
 * The one rule: a JS `number` never touches a financial value. Every amount is
 * a decimal STRING, computed with decimal.js and stored in Postgres `numeric`.
 * The pg driver returns numeric as a string, so values round-trip unchanged.
 *
 * Rounding is half-up at LINE level, then summed — never rounded once at the
 * end, which is what produces the classic one-paisa drift against an
 * accountant's own arithmetic.
 */
import Decimal from 'decimal.js'

Decimal.set({ precision: 34, rounding: Decimal.ROUND_HALF_UP, toExpNeg: -30, toExpPos: 40 })

/** Storage scales — these mirror the numeric() types in the migrations. */
export const SCALE = { money: 4, qty: 3, rate: 4, pct: 4, display: 2 } as const

export type Money = string & { readonly __brand: 'Money' }
export type Quantity = string & { readonly __brand: 'Quantity' }
export type Rate = string & { readonly __brand: 'Rate' }
export type Percent = string & { readonly __brand: 'Percent' }

type Num = string | number | Decimal
const D = (v: Num): Decimal => new Decimal(v as Decimal.Value)

export const money = (v: Num): Money => D(v).toFixed(SCALE.money) as Money
export const qty = (v: Num): Quantity => D(v).toFixed(SCALE.qty) as Quantity
export const rate = (v: Num): Rate => D(v).toFixed(SCALE.rate) as Rate
export const pct = (v: Num): Percent => D(v).toFixed(SCALE.pct) as Percent

export const ZERO_MONEY = money(0)
export const ZERO_QTY = qty(0)

/** Round to 2dp half-up, then re-widen to storage scale. */
const to2 = (v: Decimal): Decimal => v.toDecimalPlaces(SCALE.display, Decimal.ROUND_HALF_UP)

export const addMoney = (...xs: Money[]): Money =>
  money(xs.reduce<Decimal>((a, x) => a.plus(D(x)), new Decimal(0)))
export const subMoney = (a: Money, b: Money): Money => money(D(a).minus(D(b)))
export const negMoney = (a: Money): Money => money(D(a).negated())

/**
 * Two rates on one line: the supply half and the installation half.
 *
 * A two-rate BOQ prices supply and installation separately and the line is
 * worth both together. Adding them through Money would round to the money
 * scale before the quantity is applied, which loses a paisa on every long
 * pipe run.
 */
export const addRate = (...xs: Rate[]): Rate =>
  rate(xs.reduce((acc, x) => acc.plus(D(x)), D(0)))

export const addQty = (...xs: Quantity[]): Quantity =>
  qty(xs.reduce<Decimal>((a, x) => a.plus(D(x)), new Decimal(0)))
export const subQty = (a: Quantity, b: Quantity): Quantity => qty(D(a).minus(D(b)))

/**
 * A quantity at the company's own precision (docs/11: 2 or 3 decimals).
 *
 * The storage scale stays 3 either way — narrowing a numeric column is not
 * something a settings toggle may do — so this rounds the value and re-widens
 * it, which is what makes the setting's promise true without a migration.
 */
export const toPrecision = (value: Num | null | undefined, decimals: number): Quantity => {
  const places = Math.min(SCALE.qty, Math.max(0, Math.trunc(decimals)))
  return qty(D(value ?? 0).toDecimalPlaces(places, Decimal.ROUND_HALF_UP))
}

/** qty × rate, less an optional percentage discount. Rounded at the line. */
export const lineAmount = (q: Quantity, r: Rate, discountPct: Percent = pct(0)): Money => {
  const gross = D(q).times(D(r))
  const net = gross.minus(gross.times(D(discountPct)).div(100))
  return money(to2(net))
}

/**
 * A whole-document discount, applied to a line that has already had its own.
 *
 * Distributing the discount across the lines rather than subtracting it from
 * the foot is what keeps tax right: GST is charged on the discounted value of
 * each line, and each line carries its own rate.
 */
export const lessDiscount = (base: Money, discountPct: Percent): Money =>
  money(to2(D(base).minus(D(base).times(D(discountPct)).div(100))))

/** Tax on a base amount. Rounded at the tax line, per statutory practice. */
export const taxOn = (base: Money, ratePct: Percent): Money =>
  money(to2(D(base).times(D(ratePct)).div(100)))

/**
 * The GST split (docs/15 §7.1).
 *
 * Within a state the same rate is charged as CGST plus SGST at half each;
 * across states it is one IGST line. Both halves are computed from the whole
 * rather than each from half the rate, and the second half takes the
 * remainder — otherwise a tax of ₹0.05 becomes two rounded halves of ₹0.03 and
 * the parts no longer add up to the whole, which is what a tax officer checks
 * first.
 */
export const splitTax = (taxAmount: Money, interstate: boolean): { igst: Money; cgst: Money; sgst: Money } => {
  if (interstate) return { igst: taxAmount, cgst: ZERO_MONEY, sgst: ZERO_MONEY }
  const cgst = money(to2(D(taxAmount).div(2)))
  return { igst: ZERO_MONEY, cgst, sgst: money(D(taxAmount).minus(D(cgst))) }
}

/**
 * Rounding adjustment to the nearest rupee. Returned explicitly so it can be
 * shown on the document and signed off, rather than silently absorbed.
 */
/** How a currency rounds a document's total to the whole unit (SET-026). */
export type RoundingMethod = 'HALF_UP' | 'HALF_EVEN' | 'UP' | 'DOWN'

const ROUNDING_MODE: Readonly<Record<RoundingMethod, Decimal.Rounding>> = {
  HALF_UP: Decimal.ROUND_HALF_UP,
  HALF_EVEN: Decimal.ROUND_HALF_EVEN,
  // Away from zero / towards zero, so a credit rounds the way a debit does.
  UP: Decimal.ROUND_UP,
  DOWN: Decimal.ROUND_DOWN,
}

export const roundingAdjustment = (
  total: Money, method: RoundingMethod = 'HALF_UP',
): { rounded: Money; adjustment: Money } => {
  const t = D(total)
  const r = t.toDecimalPlaces(0, ROUNDING_MODE[method] ?? Decimal.ROUND_HALF_UP)
  return { rounded: money(r), adjustment: money(r.minus(t)) }
}

export const cmpMoney = (a: Money, b: Money): -1 | 0 | 1 => D(a).comparedTo(D(b)) as -1 | 0 | 1
export const cmpQty = (a: Quantity, b: Quantity): -1 | 0 | 1 => D(a).comparedTo(D(b)) as -1 | 0 | 1
export const gtQty = (a: Quantity, b: Quantity): boolean => D(a).greaterThan(D(b))
export const isZeroQty = (a: Quantity): boolean => D(a).isZero()
export const isNegQty = (a: Quantity): boolean => D(a).isNegative()
export const isZeroMoney = (a: Money): boolean => D(a).isZero()
export const isNegMoney = (a: Money): boolean => D(a).isNegative()

/** Percentage of a against b, 2dp. Returns '0.0000' when b is zero. */
export const pctOf = (a: Money, b: Money): Percent => {
  const bb = D(b)
  if (bb.isZero()) return pct(0)
  return pct(to2(D(a).div(bb).times(100)))
}

/** Indian lakh/crore grouping: 1,22,257.00 — not 122,257.00. */
export const formatINR = (v: Money | Quantity | string, opts: { symbol?: boolean; dp?: number } = {}): string => {
  const dp = opts.dp ?? SCALE.display
  const n = D(v).toDecimalPlaces(dp, Decimal.ROUND_HALF_UP)
  const neg = n.isNegative()
  const [int = '0', frac = ''] = n.abs().toFixed(dp).split('.')
  let grouped: string
  if (int.length <= 3) grouped = int
  else {
    const last3 = int.slice(-3)
    const rest = int.slice(0, -3)
    grouped = `${rest.replace(/\B(?=(\d{2})+(?!\d))/g, ',')},${last3}`
  }
  const sym = opts.symbol === false ? '' : '₹ '
  return `${neg ? '-' : ''}${sym}${grouped}${dp > 0 ? `.${frac}` : ''}`
}

export const formatQty = (v: Quantity): string => {
  const n = D(v)
  return n.equals(n.toDecimalPlaces(0)) ? formatINR(v, { symbol: false, dp: 0 }) : formatINR(v, { symbol: false, dp: 3 })
}
